"use client";

import React from "react";
import { Briefcase, Network, Settings, Utensils, Hotel } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const audiences = [{
  id: "c-suite",
  title: "Small and Mid Size Businesses",
  description: "We help growing businesses implement practical AI tools, workflow automation, and cloud technology to streamline operations, reduce costs, and scale efficiently. Whether you're a 10-person team or a 500-person organization, Cyril Consulting Group delivers AI and automation solutions built for the way small and mid-size businesses actually work — without the enterprise price tag or complexity.",
  iconName: "Briefcase"
}, {
  id: "operations",
  title: "Content Creators and Entrepreneurs\xA0",
  description: "We help content creators, influencers, and digital entrepreneurs scale content production, grow their audiences, and increase digital revenue with AI-powered automation. From content workflow automation to data-driven audience insights, Cyril Consulting Group delivers the systems that let you spend less time on repetitive tasks and more time creating and monetizing.",
  iconName: "Settings"
}, {
  id: "it-directors",
  title: "Enterprise Leaders",
  description: "We help enterprise leaders align artificial intelligence, automation, and scalable cloud infrastructure with long-term business strategy. Cyril Consulting Group partners with CIOs, CTOs, and transformation executives to navigate complex digital transformation initiatives — delivering measurable improvements in operational efficiency, competitive agility, and AI-driven revenue growth.",
  iconName: "Network"
}, {
  id: "restaurants",
  title: "Restaurants and Food Service",
  description: "We help restaurants and food service operators leverage AI, automation, and modern digital tools to improve guest experiences, streamline kitchen and front-of-house operations, and grow revenue. From POS integration and inventory automation to guest data analytics and digital ordering optimization, Cyril Consulting Group delivers practical technology strategies built for the realities of food service operations.",
  iconName: "Utensils"
}, {
  id: "hotels",
  title: "Hotels and Lodgings",
  description: "We help hotels and lodging operators modernize property technology, optimize guest services, and improve revenue performance with AI-powered insights and cloud-based solutions. From property management system modernization and revenue management analytics to automated guest communications and operational efficiency tools, Cyril Consulting Group helps hospitality leaders get more from their technology investment.",
  iconName: "Hotel"
}];
const getIcon = (name: string) => {
  const commonClasses = "w-12 h-12 text-accent-foreground mb-4 mx-auto";
  switch (name) {
    case "Briefcase":
      return <Briefcase className={commonClasses} strokeWidth={1.5} />;
    case "Settings":
      return <Settings className={commonClasses} strokeWidth={1.5} />;
    case "Network":
      return <Network className={commonClasses} strokeWidth={1.5} />;
    case "Utensils":
      return <Utensils className={commonClasses} strokeWidth={1.5} />;
    case "Hotel":
      return <Hotel className={commonClasses} strokeWidth={1.5} />;
    default:
      return null;
  }
};
export default function WhoWeHelp() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="who-we-help" className="bg-muted/30 py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-bold text-primary tracking-tight mb-4">
            Who We Help
          </h2>
          <p className="text-lg text-muted-foreground">Cyril Consulting Group partners with business leaders and organizations ready to adopt AI consulting, cloud strategy, and automation solutions that drive operational efficiency, innovation, and measurable business growth.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {audiences.map((audience, i) => {
          const {
            ref: cardRef,
            isVisible: isCardVisible
          } = useRevealOnScroll({
            threshold: 0.1
          });
          return <div key={audience.id} data-index={i} ref={cardRef} className={`transition-all duration-700 ease-out delay-[${i * 150}ms] ${isCardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                <Card className="h-full bg-card border-transparent shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:border-border/50 text-center group">
                  <CardHeader className="pt-8 pb-4">
                    <div className="transform transition-transform duration-300 group-hover:scale-110">
                      {getIcon(audience.iconName)}
                    </div>
                    <CardTitle className="text-xl font-semibold text-primary mt-4">
                      {audience.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pb-8">
                    <CardDescription className="text-base text-muted-foreground leading-relaxed">
                      {audience.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}